import React from 'react'
import { Helmet } from 'react-helmet'
import {
  DEFAULT_OG_IMAGE,
  GLOBAL_KEYWORDS,
  SITE_NAME,
  getSiteUrl,
} from '../Utils/seoConfig'

/**
 * Per-page SEO: title, description, canonical, OG/Twitter, hreflang, JSON-LD.
 *
 * @param {{
 *   title: string,
 *   description: string,
 *   path?: string,
 *   image?: string,
 *   keywords?: string,
 *   type?: string,
 *   noindex?: boolean,
 *   jsonLd?: object | object[],
 * }} props
 */
export default function Seo({
  title,
  description,
  path = '',
  image,
  keywords = GLOBAL_KEYWORDS,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  const siteUrl = getSiteUrl()
  const canonical = `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`.replace(/\/$/, '') || siteUrl
  const ogImage = image
    ? image.startsWith('http')
      ? image
      : `${siteUrl}${image.startsWith('/') ? image : `/${image}`}`
    : `${siteUrl}${DEFAULT_OG_IMAGE}`

  const ldBlocks = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : []

  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />

      {/* Bilingual / regional hints (same URL — language toggled client-side) */}
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="ar" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_SA" />
      <meta property="og:locale:alternate" content="ar_SA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ldBlocks.map((block, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  )
}
